"use strict";

import WMSGetFeatureInfo from "ol/format/WMSGetFeatureInfo";
import {fromExtent} from "ol/geom/Polygon";

/**
 * @classdesc
 * Implementierung des FeatureInfo-Providers für WMS-Layer auf Basis OpenLayers3.
 *
 * @class
 * @extends gc.gis.module.GCFeatureInfoProvider
 * @memberof gc.gis.module
 * @author Christian Schmidt <c.schmidt@brain-scc.de>
 *
 * @param {gc.gis.module.GCFeatureInfo} module Referenz auf das Feature-Info-Modul
 * @param {gc.gis.map.GCMapView} mapView
 * @param {gc.gis.factory.ObjectFactory} mapFactory
 * @param {gc.gis.util.GCRequestManager} requestManager
 * @param {gc.gis.log.GCLog} logger
 */
gc.gis.module.GCFeatureInfoProvider.OLWMSLayer = function(
    module,
    mapView,
    mapFactory,
    requestManager,
    logger)
{
    gc.gis.module.GCFeatureInfoProvider.call(this, module);

    this._SUPPORTED_INFO_FORMATS = [
        'application/vnd.ogc.gml',
        'text/html',
        'text/plain',
    ];

    /**
     * @type {gc.gis.map.GCMapView}
     * @private
     */
    this._mapView = mapView;
    /**
     * @type {gc.gis.util.GCRequestManager}
     * @private
     */
    this._requestManager = requestManager;
    /**
     * @type {gc.gis.factory.ObjectFactory}
     * @private
     */
    this._mapFactory = mapFactory;
    /**
     * @type {gc.gis.log.GCLog}
     * @private
     */
    this._logger = logger;
    /**
     * @type {gc.gis.map.GCAbstractWMSProvider}
     * @private
     */
    this._wmsProvider = this._mapFactory.createWMSProvider(this._requestManager, this._mapView.getMap());
    /**
     * Abzufragender Layer.
     * @type {gc.gis.layer.GCLayer}
     * @private
     */
    this._layer = undefined;
    /**
     * Abzufragende Geometrie.
     * @type {gc.gis.geom.GCGeometry}
     * @private
     */
    this._geometry = undefined;
    /**
     * Abzufragende Geometrie.
     * @type {string}
     * @private
     */
    this._url = undefined;
    /**
     * Abzufragende Geometrie.
     * @type {string}
     * @private
     */
    this._infoFormat = undefined;
};
gc.gis.module.GCFeatureInfoProvider.OLWMSLayer.prototype = Object.create(gc.gis.module.GCFeatureInfoProvider.prototype);


/**
 * @inheritDoc
 */
gc.gis.module.GCFeatureInfoProvider.OLWMSLayer.prototype.identify = function (layer, geometry)
{
    const self = this;
    this._layer = layer;
    this._geometry = geometry;

    const point = this._tryDetermineInfoPoint();
    if (!point)
    {
        return;
    }

    this._wmsProvider.getCapabilities(layer.getUrl(), function (capabilities) {
        const layerNames = self._layer.getName().split(',');
        const queryableLayers = self._determineQueryableLayers(capabilities, layerNames);

        if (queryableLayers.length > 0)
        {
            self._infoFormat = self._tryDetermineInfoFormat(capabilities);
            self._url = self._getFeatureInfoUrl(queryableLayers, point, geometry.getCRS());
            if (self._url)
            {
                self._doFeatureInfo();
            }
            else
            {
                throw new gc.gis.error.GCError('Failed to determine FeatureInfo-Request for wms layer "' + layer.getLabel() + '"');
            }
        }
    });

    this._fireEvent(gc.gis.module.GCFeatureInfoProviderEvent.IDENTIFYSTARTED, {
        layer: layer,
        provider: self
    });
};

gc.gis.module.GCFeatureInfoProvider.OLWMSLayer.prototype._getFeatureInfoUrl = function (queryableLayers, point, projection)
{
    const olMap = this._mapView.getMap().getImpl();
    const olView = olMap.getView();
    const olSource = this._layer.getImpl().getSource();
    const resolution = olView.getResolution();
    //const projection = olView.getProjection();

    return olSource.getFeatureInfoUrl(
        point,
        resolution,
        projection,
        {
            'INFO_FORMAT': this._infoFormat,
            'QUERY_LAYERS': queryableLayers.join(',')
        }
    );
};

/**
 * @param {Object} capabilities
 * @param {string[]} layerNames
 * @return {string[]}
 * @private
 */
gc.gis.module.GCFeatureInfoProvider.OLWMSLayer.prototype._determineQueryableLayers = function (capabilities, layerNames)
{
    const queryableLayers = [];
    const capLayers = this._wmsProvider.findLayers(capabilities, layerNames);

    for (let i = 0, ii = capLayers.length; i < ii; i++)
    {
        if (capLayers[i].queryable && capLayers[i].name)
        {
            queryableLayers.push(capLayers[i].name);
        }
    }

    return queryableLayers;
};

gc.gis.module.GCFeatureInfoProvider.OLWMSLayer.prototype._tryDetermineInfoFormat = function (capabilities)
{
    const wmsInfoFormats = capabilities.infoFormats || [];
    for (let i = 0, ii = this._SUPPORTED_INFO_FORMATS.length; i < ii; i++)
    {
        for (let j = 0, jj = wmsInfoFormats.length; j < jj; j++)
        {
            if (wmsInfoFormats[j].toLowerCase() === this._SUPPORTED_INFO_FORMATS[i].toLowerCase())
            {
                return wmsInfoFormats[j];
            }
        }
    }

    throw new gc.gis.error.GCError('The WMS does not support the expected formats: ' + this._SUPPORTED_INFO_FORMATS.join(', '));
};

gc.gis.module.GCFeatureInfoProvider.OLWMSLayer.prototype._tryDetermineInfoPoint = function ()
{
    if (this._geometry instanceof gc.gis.geom.GCPoint)
    {
        return [this._geometry.getX(), this._geometry.getY()];
    }

    this._logger.info(this, `Die Ebene "${this._layer.getLabel()}" unterstützt nur die Abfrage nach Punkt (WMS-Ebene).`);
};

gc.gis.module.GCFeatureInfoProvider.OLWMSLayer.prototype._doFeatureInfo = function ()
{
    const self = this;
    this._requestManager.ajax({
        label: 'Lade WMS-Objekt-Informationen ... ',
        context: this,
        url: this._url,
        useOGCProxy: true,
        success: function (data) {
            self._fireEvent(gc.gis.module.GCFeatureInfoProviderEvent.IDENTIFYEND, {
                layer: self._layer,
                provider: self,
                features: self._buildFeatures(data)
            });
        },
        error: function (err) {
            self._fireEvent(gc.gis.service.FeatureServiceEvent.IDENTIFY_ERROR, {
                layer: self._layer,
                error: err
            });
        }
    });
};

gc.gis.module.GCFeatureInfoProvider.OLWMSLayer.prototype._buildFeatures = function (data)
{
    if ('' === data.trim())
    {
        return [];
    }

    // TODO Parser pro Format in eigene Klasse auslagern
    if ('application/vnd.ogc.gml' === this._infoFormat.toLowerCase())
    {
        return this._buildFeaturesFromGML2(data);
    }

    return [this._buildFeatureFromRawData(this._url, gc.gis.module.GCFeatureInfoDetailResultTypes.HTML_THIRD_PARTY, 1)];
};

gc.gis.module.GCFeatureInfoProvider.OLWMSLayer.prototype._buildFeaturesFromGML2 = function (gml)
{
    const featureInfoParser = new WMSGetFeatureInfo();
    const parsedFeatures = featureInfoParser.readFeatures(gml);
    const formatedFeatures = [];

    // TODO mit VueJS implementieren
    let html, th, td, prop, formatedFeature, name, geom;
    for (let i = 0, ii = parsedFeatures.length; i < ii; i++)
    {
        const properties = parsedFeatures[i].getProperties();
        html = '';
        html += '<table class="table table-hover table-condensed">';
        html += '  <tbody>';

        name = undefined;
        geom = undefined;
        for (let key in properties)
        {
            if (!properties.hasOwnProperty(key)) {
                continue;
            }
            if ('boundedBy' === key)
            {
                geom = this._mapFactory.createPolygon(fromExtent(properties[key]))
                continue;
            }

            prop = properties[key] || '';
            // jQuery text zum unterbinden von XSS
            th = jQuery('<th>', {
                scope: "row"
            });
            th.text(key);
            td = jQuery('<td>');
            td.text(prop);

            html += '    <tr>';
            html += '      ' + th[0].outerHTML;
            html += '      ' + td[0].outerHTML;
            html += '    </tr>';
        }
        html += '  </tbody>';
        html += '</table>';

        formatedFeature = this._buildFeatureFromRawData(html, gc.gis.module.GCFeatureInfoDetailResultTypes.INLINE_HTML,i+1);
        if (geom)
        {
            formatedFeature.setGeometry(geom);
        }
        formatedFeatures.push(formatedFeature);
    }

    return formatedFeatures;
};

gc.gis.module.GCFeatureInfoProvider.OLWMSLayer.prototype._buildFeatureFromRawData = function (htmlOrPlain, contentType, numFeature)
{
    const feature = this._mapFactory.createFeature();
    const fid = gc.gis.GCUtil.hashCode(this._url) + '-' + Math.floor(Math.random() * 1000000) + '-' + numFeature;

    feature.setID(fid);
    feature.setAttribute('featureLayer', this._layer.getName().replaceAll(',','_'));
    feature.setAttribute('actions', []);
    feature.setAttribute('isShowDetail', false);
    feature.setAttribute('detailResult', {
        content: htmlOrPlain,
        contentType: contentType
    });

    return feature;
}

/**
 * @inheritDoc
 */
gc.gis.module.GCFeatureInfoProvider.OLWMSLayer.prototype.renderDetail = function (layer, features)
{
    const self = this;

    if (!Array.isArray(features))
    {
        features = [features];
    }

    this._fireEvent(gc.gis.module.GCFeatureInfoProviderEvent.DETAILSTART, {
        layer: layer,
        provider: self
    });

    for (let i = 0, ii = features.length; i < ii; i++)
    {
        // Details sind bereits vorhanden => zurückgeben
        const feature = features[i];
        const result = feature.getAttribute('detailResult');
        self._fireEvent(gc.gis.module.GCFeatureInfoProviderEvent.DETAILEND, {
            provider: self,
            layer: layer,
            feature: feature,
            detailResult: result
        });
    }
};